import { PlusOutlined, SendOutlined } from '@ant-design/icons'
import { Button, Card, Flex, Space, Table } from 'antd'
import { ColumnType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useState } from 'react'

import { useInvestigationsServiceGetInvestigationTestsEndpoint } from 'src/api/queries'
import {
  InvestigationTestResultDto,
  InvestigationTestsListItemDto,
  InvestigationTestStatus
} from 'src/api/requests'
import { ApproveTestModal, TbTag, Text } from 'src/components'
// Direct file import: this card is re-exported from the tests barrel that the
// actions component pulls the result editor from, so the barrel would cycle.
import { ResultActions } from 'src/features/persons/components/PersonInvestigations/ResultActions'
import { TestStatusTag } from 'src/features/tests-results/components'
import { getFullName } from 'src/features/users/utils'
import { useTablePagination } from 'src/hooks'
import { dayMonthYearHourMinutesFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'

import { useSendInvestigationTests } from '../hooks'

import { AddResultModal } from './AddResultModal'
import { AddTestResultModal } from './AddTestResultModal'

enum ModalEnum {
  ADD_TEST = 'add_test',
  ADD_RESULT = 'add_result'
}

type Props = {
  investigationId: string
  typeId?: string
  /** Specimen type, to offer only investigation types that accept it. */
  specimenTypeId?: string
  isFinalized?: boolean
}

export const InvestigationTestsCard = ({
  investigationId,
  typeId,
  specimenTypeId,
  isFinalized
}: Props) => {
  const { t, currentLang } = useLocales()

  const pagination = useTablePagination()
  const [modalType, setModalType] = useState<ModalEnum>()
  const [currentTestId, setCurrentTestId] = useState<string>()
  const [approvingTest, setApprovingTest] = useState<{
    id: string
    results?: InvestigationTestResultDto[]
  }>()

  const { canValidateResults, sendTest, getSendableTests } =
    useSendInvestigationTests()

  const { data, isLoading: isLoadingTests } =
    useInvestigationsServiceGetInvestigationTestsEndpoint(
      {
        investigationId
      },
      [{ investigationId: investigationId, typeId }],
      { enabled: !!investigationId }
    )

  const onCloseModal = () => {
    setModalType(undefined)
    setCurrentTestId(undefined)
  }

  const sendableTests = getSendableTests(data?.data)

  const onSendAll = () =>
    sendableTests.forEach((test) => test.id && sendTest(test.id))

  // Test type, result option, value option and numeric value, comma-separated.
  const formatResult = (result: InvestigationTestResultDto) =>
    [
      result.testType?.translation?.[currentLang?.value],
      result.resultOption?.translation?.[currentLang?.value],
      result.resultValueOption?.translation?.[currentLang?.value],
      result.resultValue,
      result.laboratoryResultStatus
    ]
      .filter((value) => value !== null && value !== undefined && value !== '')
      .join(', ')

  const columns: ColumnType<InvestigationTestsListItemDto>[] = [
    {
      key: 'code',
      title: t('investigations.test_number'),
      render: (_, { code, isTb }) => (
        <Flex gap={8} align="center">
          {code}
          <TbTag isTb={isTb} />
        </Flex>
      )
    },
    {
      key: 'creation date',
      title: t('consultations.harvest_date'),
      render: (_, { testingAt }) =>
        testingAt
          ? dayjs
              .utc(testingAt)
              .tz()
              .format(dayMonthYearHourMinutesFormatWithDot)
          : '-'
    },
    {
      key: 'result',
      title: t('analysis.result'),
      render: (
        _,
        { results, isApproved, referralDoctorUser, referralDoctorName }
      ) => {
        if (!results || results.length === 0) return '-'

        return (
          <Flex vertical gap={4}>
            {results.map((result, index) => (
              <Flex key={index} gap={4} align="center" wrap="wrap">
                <Text>{formatResult(result) || '-'}</Text>
                <ResultActions
                  result={result}
                  isApproved={isApproved}
                  referralDoctorUserId={referralDoctorUser?.id}
                  referralDoctorName={referralDoctorName}
                  referralDoctorUserName={getFullName(
                    referralDoctorUser?.firstName,
                    referralDoctorUser?.lastName
                  )}
                  typeId={typeId}
                />
              </Flex>
            ))}
          </Flex>
        )
      }
    },
    {
      key: 'result date',
      title: t('tests_results.result_date'),
      render: (_, { results }) => {
        if (!results || results.length === 0) return '-'

        return (
          <Flex vertical gap={2}>
            {results.map((result, index) => (
              <Text key={index}>
                {result.resultDate
                  ? dayjs
                      .utc(result.resultDate)
                      .tz()
                      .format(dayMonthYearHourMinutesFormatWithDot)
                  : '-'}
              </Text>
            ))}
          </Flex>
        )
      }
    },
    {
      key: 'validated by',
      title: t('tests_results.validated_by'),
      render: (_, { results }) => {
        if (!results || results.length === 0) return '-'

        return (
          <Flex vertical gap={2}>
            {results.map((result, index) => (
              <Text key={index}>
                {result.validatedByUser
                  ? getFullName(
                      result.validatedByUser.firstName,
                      result.validatedByUser.lastName
                    )
                  : '-'}
              </Text>
            ))}
          </Flex>
        )
      }
    },
    {
      key: 'referral doctor',
      title: t('investigations.referral_doctor'),
      render: (_, { referralDoctorName, referralDoctorUser }) =>
        referralDoctorName ||
        getFullName(
          referralDoctorUser?.firstName,
          referralDoctorUser?.lastName
        ) ||
        '-'
    },
    {
      key: 'status',
      title: t('form.status'),
      render: (_, { status }) => <TestStatusTag status={status} />
    },
    {
      key: 'actions',
      render: (_, { id, results, isApproved, status }) => {
        if (!id || isApproved || status === InvestigationTestStatus.REJECTED)
          return null

        const hasResult = !!results?.length

        return (
          <Space>
            {!hasResult && (
              <Button
                type="link"
                icon={<PlusOutlined />}
                onClick={() => {
                  setModalType(ModalEnum.ADD_RESULT)
                  setCurrentTestId(id)
                }}
              >
                {t('analysis.add_one_result')}
              </Button>
            )}

            {hasResult &&
              !canValidateResults &&
              status === InvestigationTestStatus.NEW && (
                <Button
                  type="link"
                  icon={<SendOutlined />}
                  onClick={() => sendTest(id)}
                >
                  {t('investigations.send_to_validation')}
                </Button>
              )}

            {/* Approving is the doctor's confirmation of the result, so it goes
                through the modal where they can still replace it. */}
            {hasResult && canValidateResults && (
              <Button
                type="link"
                icon={<SendOutlined />}
                onClick={() => setApprovingTest({ id, results })}
              >
                {t('analysis.add_analysis.send_test_result')}
              </Button>
            )}
          </Space>
        )
      }
    }
  ]

  return (
    <Card>
      <Flex vertical gap={24}>
        <Flex justify="space-between" gap={8}>
          <Text size="xl" weight={700}>
            {t('investigations.tests')}
          </Text>

          <Flex gap={8}>
            {!!sendableTests.length && (
              <Button icon={<SendOutlined />} onClick={onSendAll}>
                {t('investigations.send_all')}
              </Button>
            )}

            {!isFinalized && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setModalType(ModalEnum.ADD_TEST)}
              >
                {t('analysis.add_test_and_result')}
              </Button>
            )}
          </Flex>
        </Flex>

        <Table
          rowKey="id"
          loading={isLoadingTests}
          bordered
          dataSource={data?.data}
          columns={columns}
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>

      {modalType === ModalEnum.ADD_TEST && (
        <AddTestResultModal
          onCancel={onCloseModal}
          typeId={typeId}
          specimenTypeId={specimenTypeId}
          canChangeInvestigationType={
            !data?.data?.some((test) => !!test.results?.length)
          }
          investigationId={investigationId}
        />
      )}

      {modalType === ModalEnum.ADD_RESULT && (
        <AddResultModal
          typeId={typeId}
          testId={currentTestId}
          onCancel={onCloseModal}
        />
      )}

      {approvingTest && (
        <ApproveTestModal
          testId={approvingTest.id}
          results={approvingTest.results}
          typeId={typeId}
          onCancel={() => setApprovingTest(undefined)}
        />
      )}
    </Card>
  )
}
